import { memo, useLayoutEffect, useState } from 'react'
import { IBlockProps } from '../../../../../Block/types'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { selectRuleConfigBit } from '../../../../../../../store/selectors/ruleCardSelectors'
import { generateAriaId } from '../../../../../../ActionsBar/utils/generateAriaId'
import { useExpand } from '../../../../../../../hooks/useExpand'
import style from '../../../../../Block/Styles/index.module.scss'
import { getLabel } from '../../../../../../../utils/getLabel'
import { isOdd } from '../../../../../../../utils/math'
import { useInitialEffect } from '../../../../../../../hooks/useInitialEffect'
import { ViewOnlyWrapper } from '../../../../../../ViewOnly/ViewOnlyWrapper'
import { DescriptionBtnWithPopup } from '../../../../../../buttons/PromptBtn/DescriptionBtnWithPopup'
import { BlockDataBtn } from '../../../../../../buttons/BlockDataBnt'
import { Collapse } from 'react-bootstrap'
import { blockConstructor } from '../../../../../Block/control'
import {
    disableRuleBlock,
    enableRuleBlock,
    initRuleEmptyData,
} from '../../../../../../../store/actions/ruleCardActions'

export const RuleWrappedBlock = memo(function WrappedBlock({
    dataPath,
    pathWithElementID,
    elements,
    title,
    className,
    dataSchema = {},
    name,
    classNameBody,
    wrapper,
    buttons,
    footerData = [],
    renderHeaderControl = () => [],
    isParentDisabled,
    levelDeep = 2,
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
}: IBlockProps) {
    const {
        is_required: isRequired,
        is_auxiliary: isCollapsed,
        requiredMark,
    } = dataSchema
    const dispatch = useAppDispatch()

    const [isRendered, setIsRendered] = useState<boolean>(false)
    const path = [...dataPath.split(PATH_JOINER), name]
    const configBit = useAppSelector(selectRuleConfigBit(path))

    const [blockState, setBlockState] = useState<boolean>(
        !!isRequired || !!configBit
    )

    const dataProp = configBit ?? {}
    const childrenPath = blockState && !isParentDisabled ? path : []
    const localId = element_id ? `${element_id}-${name}` : `${path}-${name}`
    const ariaID = generateAriaId()
    const { expandBtn, expanded, toggle } = useExpand({
        isExpanded: !isCollapsed,
        uniqueID: localId,
        ariaID,
        className: `${style.button} alwaysActive`,
    })

    const label = getLabel(name!, title)
    const styleHeader = [
        style.header,
        inCard && !isRuleActive
            ? 'rule-inactive-block'
            : `block-header-${levelDeep} block-bg-header-default`,
        'p-0 rounded-top',
    ]
    const styleBody =
        inCard && !isRuleActive
            ? 'inactive-bg'
            : isOdd(levelDeep)
              ? 'bg-white'
              : 'app-bg-primary'

    const modifiedPathWithElementID = [
        ...(pathWithElementID?.split(PATH_JOINER) ?? []),
        name,
    ]
    const errorKey = modifiedPathWithElementID.join(PATH_JOINER)

    const onBlockStateToggle = () => {
        setBlockState((isEnabled) => {
            if (isEnabled && isRendered) {
                dispatch(disableRuleBlock({ path, errorKeyName: errorKey }))
            }
            if (!isEnabled && !isParentDisabled && !configBit) {
                dispatch(enableRuleBlock({ path, data: dataProp }))
            }

            return !isEnabled
        })
    }

    useLayoutEffect(() => {
        // NOTE: IMPORTANT! On block required, set empty obj in config before layout is rendered and child parameters' initial value set is triggered
        if (isRequired && !configBit) {
            dispatch(
                initRuleEmptyData({ schema: dataSchema, name: name!, path })
            )
        }

        if (!isRequired && !configBit) {
            dispatch(disableRuleBlock({ path, errorKeyName: errorKey }))
        }
    }, []) // eslint-disable-line

    useInitialEffect(() => {
        setIsRendered(true)
    })

    return (
        <ViewOnlyWrapper
            disableEvents={!!isViewOnly}
            className={`${className} ${
                !wrapper
                    ? 'width-49'
                    : 'w-100 border-left border-right border-bottom'
            } d-flex flex-column rounded mt-2 mb-2 position-relative border`}
        >
            <div className={styleHeader.join(' ')}>
                <div
                    onClick={toggle}
                    className="w-100"
                >
                    <h4
                        className={`${style.title} ${!title && style.capitalized}`}
                    >
                        {wrapper === 'block_dictionary_card_row' ? (
                            <div style={{ height: '37px' }} />
                        ) : (
                            label
                        )}{' '}
                        {isRequired && requiredMark && <span>*</span>}
                    </h4>
                </div>
                {buttons &&
                    buttons.map((element: JSX.Element, index: number) => (
                        <span
                            className="d-flex"
                            key={index + ''}
                        >
                            {element}
                        </span>
                    ))}
                {dataSchema.description && (
                    <DescriptionBtnWithPopup
                        type="small"
                        header={label}
                        description={dataSchema.description}
                        className="ms-auto me-2 text-white"
                    />
                )}
                {renderHeaderControl(blockState)?.length !== 0 &&
                    renderHeaderControl(blockState).map(
                        (el: JSX.Element, i: number) => (
                            <span key={i}>{el}</span>
                        )
                    )}
                {!isRequired &&
                    wrapper !== 'block_dictionary_card' &&
                    wrapper !== 'block_dictionary_card_row' && (
                        <BlockDataBtn
                            state={blockState}
                            onClick={onBlockStateToggle}
                        />
                    )}
                {expandBtn}
            </div>
            <Collapse in={expanded}>
                <div className="p-relative">
                    {!blockState && !isParentDisabled && !isRequired && (
                        <div className={style.blockOverlay} />
                    )}
                    <div
                        className={`${
                            !classNameBody ? 'p-2' : classNameBody
                        } ${styleBody} rounded-bottom`}
                        id={ariaID}
                    >
                        {elements
                            ? elements?.map(
                                  (element: JSX.Element, i: number) => (
                                      <div key={i}>{element}</div>
                                  )
                              )
                            : blockConstructor({
                                  dataSchema,
                                  data: configBit,
                                  dataProp,
                                  name: name,
                                  builderType: 'ruleUiBuilder',
                                  childrenPath: childrenPath.join(PATH_JOINER),
                                  dataPath,
                                  blockState,
                                  levelDeep: levelDeep || 1,
                                  element_id,
                                  inCard,
                                  isRuleActive,
                                  pathWithElementID:
                                      modifiedPathWithElementID.join(
                                          PATH_JOINER
                                      ),
                              })}
                    </div>
                    {footerData.length > 0 && (
                        <div
                            style={{ background: '#C5DBEA' }}
                            className="d-flex justify-content-end p-1"
                        >
                            {footerData}
                            {expandBtn}
                        </div>
                    )}
                </div>
            </Collapse>
        </ViewOnlyWrapper>
    )
})
